import {
    ArticleToCreate,
    Configuration,
    CountryCode,
    QvicklyCreateCheckout,
    StripeUiMode,
} from "@/types";
import { AxiosRequestConfig } from "axios";
import {
    createPaymentUrls,
    getCountryCode,
    readConfigurationProperty,
} from "./common";
import { ConfigKeys, PaymentProviderIds } from "@/constants";
type CheckoutArticle = ArticleToCreate & {
    InternalReferenceId?: number;
};
export type CreateCheckout = {
    Id: number;
    CompanyId: string;
    CheckoutUrl: string;
    ConfirmationUrl: string;
    PaymentProviderId: number;
    CountryCode: CountryCode;
    Articles: CheckoutArticle[];
    cancellationCode?: string;
    customerEmail: string;
    UiMode?: StripeUiMode;
};

export type CreateCheckoutPartial = Omit<
    CreateCheckout,
    "ConfirmationUrl" | "CheckoutUrl"
>;

interface CreateCheckoutExtended extends CreateCheckoutPartial {
    InternalReferenceId?: string;
    cancellationCode?: string;
}

// TODO: copy from embedded-booking

interface PaymentInterface {
    createCheckout(checkout: CreateCheckoutExtended): AxiosRequestConfig;
}

class QvicklyPayment implements PaymentInterface {
    private url = "/payment/billmate/v1/checkout";
    private configuration: Configuration;

    constructor(configuration: Configuration) {
        this.configuration = configuration;
    }

    createCheckout(checkout: CreateCheckoutExtended): AxiosRequestConfig {
        const { checkoutUrl, confirmationUrl } = createPaymentUrls(
            this.configuration,
            "hosted",
            Number(checkout.Id),
            checkout.customerEmail,
            checkout.cancellationCode
        );

        // Transform Articles to use InternalReference instead of ArticleId
        const transformedArticles = checkout.Articles.map((article) => ({
            ArticleTypeId: article.ArticleTypeId,
            InternalReferenceId:
                article.InternalReferenceId || article.ArticleId || checkout.Id,
            Quantity: article.Quantity,
        }));

        // @ts-ignore TODO: add country code
        let payloadData: QvicklyCreateCheckout = {
            Articles: transformedArticles,
            CompanyId: checkout.CompanyId,
            CheckoutUrl: checkoutUrl.href,
            ...(confirmationUrl
                ? { ConfirmationUrl: confirmationUrl.href }
                : {}),
            Language: this.configuration.language || "sv",
        };

        return {
            url: this.url,
            method: "POST",
            data: payloadData,
        };
    }
}

interface StripeCreateCheckout extends CreateCheckoutExtended {
    UiMode?: StripeUiMode;
    ConfirmationUrl?: string;
}

class StripePayment implements PaymentInterface {
    private url = "/payment/stripe/v1/checkout";
    private uiMode: StripeUiMode = "embedded";
    private configuration: Configuration;

    constructor(configuration: Configuration) {
        this.configuration = configuration;
    }

    createCheckout(checkout: StripeCreateCheckout): AxiosRequestConfig {
        const { confirmationUrl } = createPaymentUrls(
            this.configuration,
            "hosted",
            Number(checkout.Id),
            checkout.customerEmail,
            checkout.cancellationCode
        );

        // Transform Articles to use InternalReference instead of ArticleId
        const transformedArticles = checkout.Articles.map((article) => ({
            ArticleTypeId: article.ArticleTypeId,
            InternalReferenceId:
                article.InternalReferenceId || article.ArticleId || checkout.Id,
            Quantity: article.Quantity,
        }));

        // @ts-ignore
        let payloadData: StripeCreateCheckout = {
            Articles: transformedArticles,
            CompanyId: checkout.CompanyId,
            ...(confirmationUrl
                ? { ConfirmationUrl: confirmationUrl.href }
                : {}),
            CountryCode:
                checkout.CountryCode ||
                getCountryCode(this.configuration.language || "sv"),
            UiMode: checkout.UiMode || this.uiMode,
        };

        return {
            url: this.url,
            method: "POST",
            data: payloadData,
        };
    }
}

class Payment implements PaymentInterface {
    private configuration: Configuration;

    constructor(configuration: Configuration) {
        this.configuration = configuration;
    }

    createCheckout(
        checkout: CreateCheckoutExtended | StripeCreateCheckout
    ): AxiosRequestConfig {
        if (checkout.PaymentProviderId === PaymentProviderIds.QVICKLY) {
            return new QvicklyPayment(this.configuration).createCheckout(
                checkout
            );
        } else if (checkout.PaymentProviderId === PaymentProviderIds.STRIPE) {
            return new StripePayment(this.configuration).createCheckout(
                checkout
            );
        } else {
            throw new Error("Invalid PaymentProviderId");
        }
    }
}

export default Payment;
